import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, claimShopifyInstall } from '../../api/client';

/**
 * Shopify claim landing for this (audit) product frontend.
 *
 * A Shopify-initiated install is handled on the backend (App URL → callback), which
 * parks the store and redirects the merchant back to the frontend they started from
 * (this one, via the `shopify_return` cookie) at `/shopify/claim?shop=…`. Here — the
 * merchant is authenticated — we link the parked store to their account via the
 * backend claim API, then return them to /connect. (App Store req 2.3.1: install is
 * Shopify-initiated; no shop domain is ever typed.)
 */
export default function ShopifyClaimView() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const shop = params.get('shop');
  const errorReason = params.get('error');

  // Terminal errors that need no async work — derived during render (not set in an effect).
  const staticError = errorReason
    ? `Shopify returned an error (${errorReason}). Please reinstall from the Shopify App Store.`
    : !shop
      ? 'Missing store. Please reinstall from the Shopify App Store.'
      : null;

  const [state, setState] = useState<'working' | 'done' | 'error'>('working');
  const [claimError, setClaimError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current || staticError || !shop) return;
    ran.current = true;
    claimShopifyInstall(shop)
      .then((r) => {
        if (r.status === 'connected') {
          setState('done');
          setTimeout(() => navigate('/connect?shopify=success', { replace: true }), 800);
        } else {
          setState('error');
          setClaimError(r.error || 'Could not connect the store.');
        }
      })
      .catch((e: unknown) => {
        setState('error');
        // Show the backend's own explanation — the three failure states need
        // different actions from the merchant and used to be indistinguishable:
        // 404 nothing parked (reinstall — also what a claim on the wrong
        // deployment looks like), 410 the parked install expired, 503 the
        // lookup itself failed (retry). Falls back to the generic line only
        // when the response carried no message at all.
        setClaimError(
          (e instanceof ApiError && e.detail) || 'Could not connect the store.',
        );
      });
  }, [shop, staticError, navigate]);

  const errorMsg = staticError ?? claimError;

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-border p-6 text-center">
        {!staticError && state === 'working' && (
          <p className="text-sm text-muted-foreground">Connecting {shop}…</p>
        )}
        {!staticError && state === 'done' && (
          <p className="text-sm font-medium text-foreground">Shopify store connected. Redirecting…</p>
        )}
        {(staticError || state === 'error') && (
          <p className="text-sm font-medium text-destructive">{errorMsg}</p>
        )}
      </div>
    </div>
  );
}
